/**
 * Shared vocabulary for the stage diff views.
 *
 * The two pipelines are always called Base and Eval, in that order, so a
 * reviewer never has to remember whether "candidate" was the left or right
 * column.
 */

export const ROLE_LABELS = {
  base: "Base",
  eval: "Eval",
};

/**
 * Presence of one item in one pipeline. Typographic rather than a badge — a
 * column of filled chips reads as decoration and hides the one row that matters.
 */
export function PresenceMark({ present, isRegression = false }) {
  if (present) {
    return (
      <span className="mse-mark mse-mark--yes" title="Selected">
        ✓
      </span>
    );
  }

  return (
    <span
      className={`mse-mark ${isRegression ? "mse-mark--no" : "mse-mark--absent"}`}
      title={isRegression ? "Missing — Base selected this" : "Not selected"}
    >
      {isRegression ? "✗" : "–"}
    </span>
  );
}

/** Role header for a column or panel, with the model that produced it. */
export function RoleLabel({ role, model }) {
  return (
    <span className={`mse-role mse-role--${role}`}>
      <span className="mse-role__name">{ROLE_LABELS[role]}</span>
      {model ? <span className="mse-role__model">{model}</span> : null}
    </span>
  );
}

/**
 * A list of values one pipeline has and the other does not. `tone` drives the
 * colour: dropped is a regression, added is informational.
 */
export function DiffList({ title, values, tone }) {
  if (!values?.length) {
    return null;
  }

  return (
    <div className={`mse-difflist mse-difflist--${tone}`}>
      <h4 className="mse-difflist__title">
        {title} <span className="mse-muted-xs">({values.length})</span>
      </h4>
      <ul className="mse-difflist__items">
        {values.map((value) => (
          <li key={value}>{value}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Long output bodies are collapsed by default so the summary above them stays
 * on screen. Uses native details/summary for keyboard and screen-reader support.
 */
export function Collapsible({ title, meta, children, defaultOpen = false }) {
  return (
    <details className="mse-collapse" open={defaultOpen}>
      <summary className="mse-collapse__summary">
        <span className="mse-collapse__title">{title}</span>
        {meta ? <span className="mse-muted-xs">{meta}</span> : null}
      </summary>
      <div className="mse-collapse__body">{children}</div>
    </details>
  );
}

/** Small "changed / same" indicator for a comparison table row. */
export function ChangeMark({ changed }) {
  if (changed) {
    return <span className="mse-changemark mse-changemark--yes">changed</span>;
  }

  return <span className="mse-changemark">same</span>;
}
